import { Flex, Typography } from "antd";
import { useEffect, useMemo, useRef } from "react";
import { Chart } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  BarController,
  LineController,
  PieController,
} from "chart.js";

import { hexToRgba } from "@/utils/colors";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  BarController,
  LineController,
  PieController,
);

const ChartSection = ({ section, onScreenshot }) => {
  const chartRef = useRef(null);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: 20,
    },
    plugins: {
      legend: {
        position: section.chartType === "pie" ? "right" : "bottom",
        labels: {
          boxWidth: 20,
          padding: 15,
          font: {
            size: 12,
          },
        },
      },
    },
  };

  const data = useMemo(() => {
    if (!section.rows?.length || !section.columns?.length) return null;

    return section.chartType !== "pie"
      ? {
          labels: section.rows.map((row) => row.data[0]),
          datasets: section.columns.map((col, index) => ({
            label: col.name,
            data: section.rows.map((row) => Number(row.data[index + 1]) || 0),
            borderColor: col.color,
            borderWidth: 2,
            backgroundColor: hexToRgba(col.color, 0.3),
          })),
        }
      : {
          labels: section.columns.map((col) => col.name),
          datasets: [
            {
              label: "",
              data: section.rows[0].data
                .slice(1)
                .map((col) => Number(col) || 0)
                .slice(0, section.columns.length),
              borderColor: section.columns.map((col) => col.color),
              borderWidth: 2,
              backgroundColor: section.columns.map((col) =>
                hexToRgba(col.color, 0.3),
              ),
            },
          ],
        };
  }, [section]);

  useEffect(() => {
    if (!onScreenshot || !chartRef.current || !data) return;

    const chartInstance = chartRef.current;
    const canvas = chartInstance.canvas || chartInstance.ctx?.canvas;
    if (!canvas) return;

    // Wait for the browser to paint the chart before capturing
    const capture = () => {
      const dataUrl = canvas.toDataURL("image/png");
      onScreenshot({
        dataUrl,
        chartType: section.chartType,
        title: section.title,
      });
    };

    const raf = requestAnimationFrame(capture);
    return () => cancelAnimationFrame(raf);
  }, [data, onScreenshot, section]);

  return (
    <Flex vertical gap={5}>
      <Typography.Title level={4}>{section.title}</Typography.Title>
      {data && (
        <div
          className={`chart-container chart-${section.chartType}`}
          style={{
            position: "relative",
            width: "100%",
            height: section.chartType === "pie" ? "400px" : "700px",
          }}
        >
          <Chart
            ref={chartRef}
            type={section.chartType}
            data={data}
            options={options}
          />
        </div>
      )}
    </Flex>
  );
};

export default ChartSection;
